/**
 * A mailbox's identities across domains (docs/features/mail.md "Addresses, aliases and groups"): which addresses it
 * can send as (Gmail's "Send mail as"), which From a reply starts with (Gmail's "reply from the same address the
 * message was sent to"), which addresses are "me", and the Google Workspace import's rules (internal aliases, the
 * Workspace's domains, which aliases can be routed, groups and who may post to them).
 */
import type { MailGroupPostPolicy, MailReplyFrom, MailSendAs } from "@bundle/contracts";

/** One of the workspace's mail domains as identity rules need it. `aliasOf`: the domain NAME it is an alias of. */
export interface IdentityDomain {
  domain: string;
  canSend: boolean;
  aliasOf?: string | undefined;
}

/** Everything that makes up a mailbox's addresses. */
export interface IdentityInput {
  address: string;
  displayName: string;
  /** Routing aliases (Settings → Email → Users & forwarding, or imported from Google). */
  aliases: string[];
  domains: IdentityDomain[];
  /** Gmail send-as addresses imported for the mailbox; `default` = Gmail's default From. */
  imported: { address: string; name?: string | undefined; default?: boolean | undefined }[];
  /** Groups the mailbox is a member of. */
  groups: { address: string; name?: string | undefined }[];
  /** My chosen default From (Settings → You › Mail, MailSendSettings.defaultFrom): wins when it's one of the list. */
  defaultFrom?: string | undefined;
}

const lower = (a: string) => a.trim().toLowerCase();
const domainOf = (a: string) => lower(a).slice(lower(a).lastIndexOf("@") + 1);
const localOf = (a: string) => lower(a).slice(0, Math.max(0, lower(a).lastIndexOf("@")));

/**
 * Every address that is this mailbox's, in order (primary, aliases, the same names on alias domains, imported
 * send-as on the workspace's domains, groups), without duplicates and whether or not the domain can send yet.
 */
export function ownAddresses(input: IdentityInput): { address: string; name?: string; group?: boolean }[] {
  const byName = new Map(input.domains.map((d) => [lower(d.domain), d]));
  const out: { address: string; name?: string; group?: boolean }[] = [];
  const seen = new Set<string>();
  const add = (address: string, extra: { name?: string | undefined; group?: boolean } = {}) => {
    const a = lower(address);
    if (seen.has(a)) {
      // An alias Gmail also lists as send-as keeps Gmail's name for it.
      const known = out.find((o) => o.address === a);
      if (known !== undefined && known.name === undefined && extra.name) known.name = extra.name;
      return;
    }
    if (a.indexOf("@") <= 0) return;
    seen.add(a);
    const e: { address: string; name?: string; group?: boolean } = { address: a };
    if (extra.name) e.name = extra.name;
    if (extra.group) e.group = true;
    out.push(e);
  };
  const own = [input.address, ...input.aliases];
  for (const a of own) add(a);
  // Google's domain aliases: anyone@alias is the same name @ the domain it aliases.
  for (const a of own) {
    for (const d of input.domains) if (d.aliasOf !== undefined && lower(d.aliasOf) === domainOf(a)) add(`${localOf(a)}@${lower(d.domain)}`);
  }
  for (const s of input.imported) if (byName.has(domainOf(s.address))) add(s.address, { name: s.name });
  for (const g of input.groups) add(g.address, { name: g.name, group: true });
  return out;
}

/**
 * The composer's From list (Mailbox.sendAs): the mailbox's own address always, the others only on domains that can
 * send. Names: the imported send-as name, a group's name, else the mailbox's. Exactly one is `default`: my chosen
 * `defaultFrom` when it's listed, else Gmail's imported default when it's listed, else the mailbox's address.
 */
export function sendableAddresses(input: IdentityInput): MailSendAs[] {
  const byName = new Map(input.domains.map((d) => [lower(d.domain), d]));
  const primary = lower(input.address);
  const list = ownAddresses(input).filter((a) => a.address === primary || byName.get(domainOf(a.address))?.canSend === true);
  const listed = (a: string | undefined) => (a !== undefined && list.some((x) => x.address === lower(a)) ? lower(a) : undefined);
  const def = listed(input.defaultFrom) ?? listed(input.imported.find((s) => s.default === true)?.address) ?? primary;
  return list.map((a) => {
    const s: MailSendAs = { address: a.address, name: a.name ?? input.displayName };
    if (a.address === def) s.default = true;
    if (a.group) s.group = true;
    return s;
  });
}

/**
 * The From list the API gives the composer (Mailbox.sendAs): `sendableAddresses`, but without the mailbox's own
 * address while its domain can't send yet (ownership verified with the mail still at Google, say, and no DKIM or
 * return-path records), so a verified sendable address of it (an alias, the same name on an alias domain, a group)
 * becomes the default instead. Empty: nothing of the mailbox can send yet (the composer says so, `fromNotSetUp`).
 */
export function composerFromList(input: IdentityInput): MailSendAs[] {
  const primary = lower(input.address);
  const list = sendableAddresses(input);
  if (input.domains.find((d) => lower(d.domain) === domainOf(primary))?.canSend !== false) return list;
  const rest = list.filter((s) => s.address !== primary);
  if (rest.length === 0 || rest.some((s) => s.default === true)) return rest;
  const pick = rest.find((s) => s.group !== true) ?? rest[0]!;
  return rest.map((s) => (s === pick ? { ...s, default: true } : s));
}

/**
 * The domain the composer can't send from yet ("Sending from bigsmall.xyz isn't set up yet"), or undefined when it
 * can: `from` (else the mailbox's default) isn't in the mailbox's From list. A mailbox without a list (an older
 * server) is never blocked here; the server still refuses.
 */
export function fromNotSetUp(mailbox: { address: string; sendAs?: MailSendAs[] | undefined }, from: string | undefined): string | undefined {
  const list = mailbox.sendAs;
  if (list === undefined) return undefined;
  const a = lower(from ?? list.find((s) => s.default === true)?.address ?? mailbox.address);
  return list.some((s) => lower(s.address) === a) ? undefined : domainOf(a);
}

/**
 * Checks a requested From (SendMailRequest.from): `ok` with the address and name to send as, or why not —
 * `not_yours` (not one of the mailbox's addresses) or `cannot_send` (its domain isn't verified for sending).
 */
export function checkFrom(from: string | undefined, input: IdentityInput): { ok: true; address: string; name: string } | { ok: false; reason: "not_yours" | "cannot_send" } {
  const list = sendableAddresses(input);
  if (from === undefined || from.trim() === "") {
    const d = list.find((s) => s.default === true) ?? { address: lower(input.address), name: input.displayName };
    return { ok: true, address: d.address, name: d.name ?? input.displayName };
  }
  const a = lower(from);
  const hit = list.find((s) => s.address === a);
  if (hit !== undefined) {
    const d = input.domains.find((x) => lower(x.domain) === domainOf(a));
    if (a === lower(input.address) || d?.canSend === true) return { ok: true, address: a, name: hit.name ?? input.displayName };
  }
  return ownAddresses(input).some((o) => o.address === a) ? { ok: false, reason: "cannot_send" } : { ok: false, reason: "not_yours" };
}

/**
 * The From a reply starts with (Gmail's "When replying to a message"). `mode` "same" (the default, "Reply from the
 * same address the message was sent to"): my own message → the address I sent it from; otherwise the first of my
 * send-as addresses it was sent to (To, then Cc, then the group it came through, then the address it was delivered
 * to); else the default. "default" ("Always reply from default address"): the default, whatever it was sent to.
 */
export function replyFrom(
  original: { direction: "in" | "out"; from: { address: string }; to: { address: string }[]; cc: { address: string }[]; via?: string | undefined; deliveredTo?: string | undefined },
  sendAs: MailSendAs[],
  mode: MailReplyFrom = "same",
): string | undefined {
  const fallback = sendAs.find((s) => s.default === true)?.address ?? sendAs[0]?.address;
  if (mode === "default") return fallback;
  const mine = new Set(sendAs.map((s) => lower(s.address)));
  if (original.direction === "out" && mine.has(lower(original.from.address))) return lower(original.from.address);
  for (const a of [...original.to.map((x) => x.address), ...original.cc.map((x) => x.address), ...(original.via === undefined ? [] : [original.via]), ...(original.deliveredTo === undefined ? [] : [original.deliveredTo])]) {
    if (mine.has(lower(a))) return lower(a);
  }
  return fallback;
}

/** Is `address` one of `mine` (case-insensitive)? ("me" in lists and threads, Sent for imported history.) */
export function isOwnAddress(address: string, mine: string[]): boolean {
  const a = lower(address);
  return mine.some((m) => lower(m) === a);
}

// ---------------------------------------------------------------------------------------------------------------
// Google Workspace import

/**
 * Google's own addresses that aren't real mail domains: the `*.test-google-a.com` alias every user gets, and
 * consumer Gmail domains.
 */
export function isGoogleInternalAddress(address: string): boolean {
  const d = domainOf(address);
  return d.endsWith(".test-google-a.com") || d === "test-google-a.com" || d.endsWith(".guest.google") || d === "googlemail.com" || d.endsWith(".googlemail.com") || d === "gmail.com";
}

/**
 * The Google Workspace's domains: from Directory domains.list when it was readable (`directory`, with domain
 * aliases), else derived from the users' addresses (primary and aliases, internal ones left out). Sorted, primary
 * first.
 */
export function googleWorkspaceDomains(
  users: { email: string; aliases?: string[] | undefined }[],
  directory?: { domain: string; primary?: boolean | undefined; aliases?: string[] | undefined }[] | undefined,
): { domain: string; aliasOf?: string; primary?: boolean }[] {
  const out = new Map<string, { domain: string; aliasOf?: string; primary?: boolean }>();
  for (const d of directory ?? []) {
    const name = lower(d.domain);
    if (isGoogleInternalAddress(`x@${name}`)) continue;
    out.set(name, d.primary === true ? { domain: name, primary: true } : { domain: name });
    for (const a of d.aliases ?? []) if (!isGoogleInternalAddress(`x@${a}`)) out.set(lower(a), { domain: lower(a), aliasOf: name });
  }
  for (const u of users) {
    for (const a of [u.email, ...(u.aliases ?? [])]) {
      const d = domainOf(a);
      if (d !== "" && !isGoogleInternalAddress(a) && !out.has(d)) out.set(d, { domain: d });
    }
  }
  return [...out.values()].sort((a, b) => Number(b.primary === true) - Number(a.primary === true) || a.domain.localeCompare(b.domain));
}

/**
 * Which of a Google user's addresses become routing aliases of their Bundle mailbox: those on one of the
 * workspace's mail domains (alias domains included) other than the mailbox's own address. The rest need their
 * domain added first (`needsDomain`, sorted domain names).
 */
export function aliasRouting(addresses: string[], mailboxAddress: string, workspaceDomains: string[]): { route: string[]; needsDomain: string[] } {
  const ours = new Set(workspaceDomains.map(lower));
  const route: string[] = [];
  const needs = new Set<string>();
  for (const raw of addresses) {
    const a = lower(raw);
    if (a === lower(mailboxAddress) || a.indexOf("@") <= 0 || isGoogleInternalAddress(a) || route.includes(a)) continue;
    if (ours.has(domainOf(a))) route.push(a);
    else needs.add(domainOf(a));
  }
  return { route, needsDomain: [...needs].sort() };
}

/**
 * Domains to suggest adding in Settings → Email (the import found them, the workspace doesn't have them): a Google
 * domain alias whose primary domain is the workspace's comes with `aliasOf` (that primary's name) pre-set.
 */
export function suggestedDomains(needsDomain: string[], googleDomains: { domain: string; aliasOf?: string | undefined }[], workspaceDomains: string[]): { domain: string; aliasOf?: string }[] {
  const ours = new Set(workspaceDomains.map(lower));
  const out: { domain: string; aliasOf?: string }[] = [];
  for (const d of [...new Set(needsDomain.map(lower))].sort()) {
    if (ours.has(d)) continue;
    const g = googleDomains.find((x) => lower(x.domain) === d);
    out.push(g?.aliasOf !== undefined && ours.has(lower(g.aliasOf)) ? { domain: d, aliasOf: lower(g.aliasOf) } : { domain: d });
  }
  return out;
}

/**
 * Google Groups' whoCanPostMessage → ours. Anyone on the internet → anyone; the organization → organization; members,
 * managers, owners and "nobody" → members (the closest Bundle has). Unknown or absent: anyone (Google's default for a
 * new group's "contact" address is decided by the admin; we don't lock mail out).
 */
export function groupPostPolicyFromGoogle(whoCanPostMessage: string | undefined): MailGroupPostPolicy {
  switch (whoCanPostMessage) {
    case "ANYONE_CAN_POST":
      return "anyone";
    case "ALL_IN_DOMAIN_CAN_POST":
      return "organization";
    case "ALL_MEMBERS_CAN_POST":
    case "ALL_MANAGERS_CAN_POST":
    case "ALL_OWNERS_CAN_POST":
    case "NONE_CAN_POST":
      return "members";
    default:
      return "anyone";
  }
}

/**
 * Whether `sender` may send to a group: anyone; organization = an address on one of the workspace's domains (and,
 * when the edge's DMARC verdict is known, not a DMARC failure); members = one of the group's member addresses.
 */
export function canPostToGroup(policy: MailGroupPostPolicy, sender: string, members: string[], workspaceDomains: string[], dmarc?: string | undefined): boolean {
  if (policy === "anyone") return true;
  if (dmarc === "fail") return false;
  const s = lower(sender);
  if (policy === "organization") return workspaceDomains.some((d) => lower(d) === domainOf(s));
  return members.some((m) => lower(m) === s);
}

/** Google groups not worth importing as mail groups: empty ones and Classroom's automatic teacher groups. */
export function skipGoogleGroup(group: { email: string; name?: string | undefined; members: number }): boolean {
  return group.members === 0 || /classroom|googlesaladeaula|sala_de_aula/i.test(localOf(group.email));
}

/**
 * The Domains step's proposal (before the admin saves a choice): import the primary domain and its aliases; other
 * domains (secondary ones) stay unchecked. Without domains.list (no primary known) every domain found is proposed.
 */
export function defaultDomainChoice(domains: { domain: string; aliasOf?: string | undefined; primary?: boolean | undefined }[]): { domain: string; import: boolean; aliasOf?: string }[] {
  const primary = domains.find((d) => d.primary === true)?.domain;
  return domains.map((d) => {
    const out: { domain: string; import: boolean; aliasOf?: string } = { domain: lower(d.domain), import: primary === undefined || d.domain === primary || d.aliasOf === primary };
    if (d.aliasOf !== undefined) out.aliasOf = lower(d.aliasOf);
    return out;
  });
}

/**
 * Whether an address's domain is imported: `choice` is the admin's saved Domains step (null = not saved yet: nothing
 * is filtered). Google-internal addresses never are.
 */
export function domainImported(address: string, choice: { domain: string; import: boolean }[] | null | undefined): boolean {
  if (isGoogleInternalAddress(address)) return false;
  if (choice === null || choice === undefined) return true;
  const d = domainOf(address);
  return choice.some((c) => c.import && lower(c.domain) === d);
}

/**
 * Settings → Email's "Add" suggestions from Google imports: domains an admin chose to import (the Domains step) that
 * the workspace doesn't have yet, with `aliasOf` (a domain NAME) when Google says it is an alias. Sorted, no repeats.
 */
export function importDomainSuggestions(
  connections: { google?: { domains?: { domain: string; import: boolean; aliasOf?: string | undefined }[] | undefined } | undefined }[],
  workspaceDomains: string[],
): { domain: string; aliasOf?: string }[] {
  const ours = new Set(workspaceDomains.map(lower));
  const out = new Map<string, { domain: string; aliasOf?: string }>();
  for (const c of connections) {
    for (const d of c.google?.domains ?? []) {
      const name = lower(d.domain);
      if (!d.import || ours.has(name) || out.has(name) || isGoogleInternalAddress(`x@${name}`)) continue;
      out.set(name, d.aliasOf === undefined ? { domain: name } : { domain: name, aliasOf: lower(d.aliasOf) });
    }
  }
  return [...out.values()].sort((a, b) => a.domain.localeCompare(b.domain));
}

/**
 * The sync card's identity line for a Google connection: how many aliases its accounts have, the domains imported
 * (chosen ones, or the proposal), how many groups were found, and the optional scopes still missing.
 */
export function googleImportSummary(google: {
  accounts: { aliases?: string[] | undefined }[];
  domains?: { domain: string; import: boolean }[] | undefined;
  groups?: { needsDomain?: boolean | undefined }[] | undefined;
  missingScopes?: string[] | undefined;
}): { aliases: number; domains: string[]; groups: number; missingScopes: string[] } {
  return {
    aliases: google.accounts.reduce((n, a) => n + (a.aliases?.length ?? 0), 0),
    domains: (google.domains ?? []).filter((d) => d.import && !isGoogleInternalAddress(`x@${d.domain}`)).map((d) => lower(d.domain)),
    groups: (google.groups ?? []).filter((g) => g.needsDomain !== true).length,
    missingScopes: google.missingScopes ?? [],
  };
}

/** The scope the Google import's Calendar part needs (everyone's own calendars, read-only). */
export const GOOGLE_CALENDAR_SCOPE = "https://www.googleapis.com/auth/calendar.readonly";

/**
 * The scopes Bundle's Google import asks for (domain-wide delegation): Gmail and users are required; domains, groups,
 * group settings and calendars are optional (without them the Domains step is derived from addresses, groups are
 * skipped and calendars wait).
 */
export const GOOGLE_IMPORT_SCOPES = [
  "https://www.googleapis.com/auth/gmail.readonly",
  "https://www.googleapis.com/auth/admin.directory.user.readonly",
  "https://www.googleapis.com/auth/admin.directory.domain.readonly",
  "https://www.googleapis.com/auth/admin.directory.group.readonly",
  "https://www.googleapis.com/auth/apps.groups.settings",
  GOOGLE_CALENDAR_SCOPE,
];
